import React from 'react';
import type { ObservationLearningGoal } from '../../types';
import { groupByThemeAndAim, THEME_COLORS, THEME_LABELS } from '../learning-goals/themes';

interface ObservationLearningGoalsProps {
  goals: ObservationLearningGoal[];
  /** Staff see goal numbers ("3.1"); parents get a plain-English intro instead. */
  variant: 'staff' | 'parent';
  authorName: string;
}

function countLabel(count: number, variant: 'staff' | 'parent'): string {
  const noun = count === 1 ? 'goal' : 'goals';
  return variant === 'staff' ? `${count} ${noun} linked` : `${count} ${noun}`;
}

const ObservationLearningGoals: React.FC<ObservationLearningGoalsProps> = ({
  goals,
  variant,
  authorName,
}) => {
  if (goals.length === 0) return null;

  const themeGroups = groupByThemeAndAim(goals);

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Learning goals · Aistéar
        </h2>
        <span
          className="text-xs text-gray-500"
          data-testid="observation-detail-learning-goals-count"
        >
          {countLabel(goals.length, variant)}
        </span>
      </div>

      {variant === 'parent' && (
        <p className="text-sm text-gray-500">
          The Aistéar early-years curriculum goals {authorName} linked to this observation.
        </p>
      )}

      {themeGroups.map(({ theme, aims }) => {
        const colors = THEME_COLORS[theme];
        return (
          <div
            key={theme}
            className="space-y-2"
            data-testid={`observation-detail-learning-goals-theme-${theme}`}
          >
            <span
              className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${colors.bg} ${colors.text}`}
            >
              {THEME_LABELS[theme]}
            </span>
            {aims.map((aim) => (
              <div key={aim.aimNumber} className="space-y-1.5">
                <p className="text-xs text-gray-500">
                  Aim {aim.aimNumber} · {aim.aimText}
                </p>
                <ul className="space-y-1.5">
                  {aim.goals.map((goal) => (
                    <li
                      key={goal.id}
                      data-testid={`observation-detail-learning-goal-${goal.id}`}
                      className="flex gap-2.5 items-start text-sm text-gray-800"
                    >
                      <span
                        aria-hidden="true"
                        className={`mt-[7px] h-1.5 w-1.5 flex-shrink-0 rounded-full ${colors.bar}`}
                      />
                      <span>
                        {variant === 'staff' && (
                          <span className="text-gray-500 mr-1.5">
                            {goal.aimNumber}.{goal.goalIndex}
                          </span>
                        )}
                        {goal.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default ObservationLearningGoals;
